import { createApi } from '@reduxjs/toolkit/query/react';
import {
    appEndpoint,
    orgEndpoint,
    releaseEndpoint,
    stepConfigEndpoint,
    stepRunEndpoint,
    workflowEndpoint,
    workflowRunEndpoint,
} from '@/helpers/apiHelpers';
import {
    AutomationType,
    AppHome,
    AppSettings,
    AppStoreAvailableBuild,
    BuildDistroBuild,
    BuildDistroSettings,
    IssueSourceType,
    OrgBuildDistroBucketsResponse,
    OrgOverviewAvailableMetricsResponse,
    OrgOverviewV2,
    ReleaseType,
    RunwayModelsResponse,
    TimelineEvent,
    TimelineEventType,
    TriageTicketFilters,
    UserEmailNotificationSettings,
} from '@/types';
import { type BaseAPIResponse, baseQuery } from './common';
import qs from 'query-string';
import type { GetPendingAssociationTicketRequest, GetPendingAssociationTicketResponse } from '@/actions/releaseStep';
import type {
    OrgInsightsResponse,
    OrgOverviewEntityRequest,
    OrgOverviewFiltersV2,
    WorkflowOverviewEntityRequest,
} from '@/actions/entities';
import type { WorkflowOverview } from '@/types/workflowOverview';
import type { RootState } from '@/reducers/reducers';
import { selectRelease } from '@/selectors/selectors';
import { updateStepRuns } from '@/actions/stepRun';
import { stepRunsSummaryActions } from '@/reducers/stepRunsSummary';

export interface GetStepRunsSummaryRequest {
    orgId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunIds: string[];
}

export interface GetRetainedBuildOptionsRequest {
    orgId: string;
    workflowId: string;
    stepConfigId: string;
}

export interface RetainedBuildOptions {
    availableBuilds: AppStoreAvailableBuild[];
    selectedRetainedBuildIds: string[];
}

export interface RetryAutomationRequest {
    orgId: string;
    appId: string;
    releaseId?: string;
    workflowId: string;
    workflowRunId: string;
    stepRunId: string;
    automationRunId: string;
    automationType?: AutomationType;
    eventData?: TimelineEvent['eventData'];
    eventType?: TimelineEventType;
}

const HOTFIX_SUPPORTED_RETRYABLE_AUTOMATIONS = [
    AutomationType.AutoKickoffRelease,
    AutomationType.AutoCherryPickFromWorkingBranch,
    AutomationType.AutoBumpVersion,
];

type BaseRequest = {
    orgId: string;
    appId: string;
};

export const defaultApi = createApi({
    reducerPath: 'defaultApi',
    baseQuery: baseQuery,
    tagTypes: [
        'AppHome',
        'PendingAssociationTickets',
        'WorkflowOverview',
        'StepRunsSummary',
        'OrgOverview',
        'UserEmailNotificationSettings',
    ],
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getAppHome: build.query<BaseAPIResponse<AppHome>, BaseRequest>({
            query: ({ orgId, appId }) => {
                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/appHome`;
            },
            providesTags: (_result, _error, { orgId, appId }) => [{ type: 'AppHome', id: `${orgId}-${appId}` }],
        }),
        getRetainedBuildOptions: build.query<BaseAPIResponse<RetainedBuildOptions>, GetRetainedBuildOptionsRequest>({
            query: ({ orgId, workflowId, stepConfigId }) =>
                `${stepConfigEndpoint({ orgId, workflowId, stepConfigId })}/retainedBuildOptions`,
            keepUnusedDataFor: 60,
        }),
        getIssueTrackingLabels: build.query<BaseAPIResponse<{ labels: string[] }>, BaseRequest>({
            query: ({ orgId, appId }) => {
                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/issueTrackingLabels`;
            },
            keepUnusedDataFor: 3 * 60,
        }),
        getOrgOverview: build.query<BaseAPIResponse<OrgOverviewV2>, OrgOverviewEntityRequest<OrgOverviewFiltersV2>>({
            query: ({ orgId, filters = {} }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/overview`,
                    method: 'POST',
                    body: {
                        ...filters,
                    },
                };
            },
            providesTags: (_result, _error, { orgId, appIds, filters }) => [
                {
                    type: 'OrgOverview',
                    id: `${orgId}-${appIds.join(',')}-${filters?.startDate ?? ''}-${filters?.endDate ?? ''}`,
                },
            ],
        }),
        getOrgInsights: build.query<BaseAPIResponse<OrgInsightsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/insights`;
            },
        }),
        getOrgAvailableMetrics: build.query<BaseAPIResponse<OrgOverviewAvailableMetricsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/availableMetrics`;
            },
        }),
        getOrgBuildDistroBuckets: build.query<BaseAPIResponse<OrgBuildDistroBucketsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/buildDistro/buckets`;
            },
        }),
        updateBuildDistroSettings: build.mutation<
            BaseAPIResponse<BuildDistroSettings>,
            { orgId: string; settings: BuildDistroSettings }
        >({
            query: ({ orgId, settings }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/updateBuildDistroSettings`,
                    method: 'POST',
                    body: settings,
                };
            },
        }),
        updateTriageDisabledSources: build.mutation<
            BaseAPIResponse<AppSettings>,
            { orgId: string; appId: string; disabledSources: IssueSourceType[] }
        >({
            query: ({ orgId, appId, disabledSources }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/triageDisabledSources`,
                    method: 'PUT',
                    body: { disabledSources },
                };
            },
        }),
        updateTriageTicketFilters: build.mutation<
            BaseAPIResponse<AppSettings>,
            { orgId: string; appId: string; filters: TriageTicketFilters }
        >({
            query: ({ orgId, appId, filters }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/triageTicketFilters`,
                    method: 'PUT',
                    body: filters,
                };
            },
        }),
        toggleExternalBuildLinkEnabled: build.mutation<
            BaseAPIResponse<BuildDistroBuild>,
            { orgId: string; appId: string; bucketId: string; buildId: string; publicBuildLinkEnabled: boolean }
        >({
            query: ({ orgId, appId, bucketId, buildId, publicBuildLinkEnabled }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/build-distribution/bucket/${bucketId}/build/${buildId}/toggleExternalBuildLinkEnabled`,
                    method: 'POST',
                    body: { publicBuildLinkEnabled },
                };
            },
        }),
        getUserEmailNotificationSettings: build.query<
            BaseAPIResponse<UserEmailNotificationSettings>,
            { orgId: string }
        >({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/userEmailNotificationSettings`;
            },
            providesTags: (_result, _error, { orgId }) => [{ type: 'UserEmailNotificationSettings', id: orgId }],
        }),
        updateUserEmailNotificationSettings: build.mutation<
            BaseAPIResponse<null>,
            { orgId: string; settings: UserEmailNotificationSettings }
        >({
            query: ({ orgId, settings }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/userEmailNotificationSettings`,
                    method: 'POST',
                    body: { emailNotificationSettings: settings },
                };
            },
            invalidatesTags: (_result, _error, { orgId }) => [{ type: 'UserEmailNotificationSettings', id: orgId }],
        }),
        getPendingAssociationTickets: build.query<
            GetPendingAssociationTicketResponse,
            GetPendingAssociationTicketRequest
        >({
            query: ({ orgId, appId, releaseId, searchCriteria }) => {
                const encodedQueryParams = qs.stringify({ searchCriteria }, { skipNull: true, skipEmptyString: true });
                const releaseUrl = releaseEndpoint(orgId, appId, releaseId);
                return `${releaseUrl}/ticketsPendingAssociation?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, appId, releaseId }) => [
                { type: 'PendingAssociationTickets', id: `${orgId}-${appId}-${releaseId}` },
            ],
        }),
        getWorkflowOverview: build.query<BaseAPIResponse<WorkflowOverview>, WorkflowOverviewEntityRequest>({
            query: ({ orgId, workflowId, destinationStepIds, excludedDestinationStepIds }) => {
                const params: Record<string, string[]> = {};
                if (destinationStepIds?.length) params.destinationStepIds = destinationStepIds;
                if (excludedDestinationStepIds?.length) params.excludedDestinationStepIds = excludedDestinationStepIds;
                const encodedQueryParams = qs.stringify(params);
                const workflowUrl = workflowEndpoint({ orgId, workflowId });
                return encodedQueryParams ? `${workflowUrl}/overview?${encodedQueryParams}` : `${workflowUrl}/overview`;
            },
            providesTags: (_result, _error, { workflowId }) => [{ type: 'WorkflowOverview', id: workflowId }],
        }),
        getStepRunsSummary: build.query<RunwayModelsResponse, GetStepRunsSummaryRequest>({
            query: ({ orgId, workflowId, workflowRunId, stepRunIds }) => {
                const base = workflowRunEndpoint({ orgId, workflowId, workflowRunId });
                const queryParams = qs.stringify({ stepRunIds: stepRunIds.join(',') });
                return `${base}/stepGroupSummary?${queryParams}`;
            },
            keepUnusedDataFor: 3 * 60,
            transformResponse: (response: BaseAPIResponse<RunwayModelsResponse>) => response.data,
            providesTags: (_result, _error, { workflowRunId, stepRunIds }) => [
                { type: 'StepRunsSummary', id: `${workflowRunId}-${stepRunIds.join(',')}` },
            ],
        }),
        retryAutomation: build.mutation<BaseAPIResponse<RunwayModelsResponse>, RetryAutomationRequest>({
            queryFn: async (request, api, _extraOptions, fetchWithBQ) => {
                const state = api.getState() as RootState;
                const release = request.releaseId ? selectRelease(state, request.releaseId) : null;
                const shouldUseHotfixRetryEndpoint =
                    release?.type === ReleaseType.Hotfix &&
                    !!request.automationType &&
                    HOTFIX_SUPPORTED_RETRYABLE_AUTOMATIONS.includes(request.automationType);

                const url =
                    shouldUseHotfixRetryEndpoint && request.releaseId ?
                        `${releaseEndpoint(request.orgId, request.appId, request.releaseId)}/automation/retryHotfixAutomation`
                    :   `${stepRunEndpoint(request)}/retryAutomation/${request.automationRunId}`;

                const result = await fetchWithBQ({
                    url,
                    method: 'POST',
                    body:
                        shouldUseHotfixRetryEndpoint ?
                            {
                                automationType: request.automationType,
                                eventData: request.eventData,
                                eventType: request.eventType,
                            }
                        :   {
                                automationRunId: request.automationRunId,
                            },
                });

                if (result.error) {
                    return { error: result.error };
                }

                return { data: result.data as BaseAPIResponse<RunwayModelsResponse> };
            },
            async onQueryStarted(_request, { dispatch, queryFulfilled }) {
                let response: BaseAPIResponse<RunwayModelsResponse>;
                try {
                    ({ data: response } = await queryFulfilled);
                } catch {
                    return;
                }

                if (response.data?.stepRuns) {
                    dispatch(updateStepRuns(response.data.stepRuns));
                    response.data.stepRuns.forEach((stepRun) => {
                        dispatch(stepRunsSummaryActions.updateStepRun(stepRun));
                    });
                }
            },
        }),
    }),
});

export const invalidateAppHomeCache = (args: { orgId: string; appId: string }) => {
    console.log(`Invalid app home cache for ${args.appId}`);
    return defaultApi.util.invalidateTags([{ type: 'AppHome', id: `${args.orgId}-${args.appId}` }]);
};

export const invalidateWorkflowOverviewCache = (args: { workflowId: string }) => {
    return defaultApi.util.invalidateTags([{ type: 'WorkflowOverview', id: args.workflowId }]);
};

export const {
    useGetAppHomeQuery,
    useGetIssueTrackingLabelsQuery,
    useGetOrgAvailableMetricsQuery,
    useGetOrgBuildDistroBucketsQuery,
    useLazyGetOrgInsightsQuery,
    useLazyGetOrgOverviewQuery,
    useGetPendingAssociationTicketsQuery,
    useGetRetainedBuildOptionsQuery,
    useGetUserEmailNotificationSettingsQuery,
    useUpdateUserEmailNotificationSettingsMutation,
    useGetWorkflowOverviewQuery,
    useLazyGetStepRunsSummaryQuery,
    useRetryAutomationMutation,
    useUpdateBuildDistroSettingsMutation,
    useUpdateTriageDisabledSourcesMutation,
    useUpdateTriageTicketFiltersMutation,
    useToggleExternalBuildLinkEnabledMutation,
} = defaultApi;
